import { Compass, Eye, Lightbulb, Sparkles, Paintbrush, Crosshair, Landmark } from "lucide-react";
import { SEO } from "@/components/SEO";
import { Reveal, Eyebrow } from "@/components/Reveal";

const VALUES = [
    { icon: Lightbulb, title: "Innovation", text: "We explore emerging technologies and turn them into practical digital products." },
    { icon: Sparkles, title: "Simplicity", text: "Complex technology should feel simple and accessible to users." },
    { icon: Paintbrush, title: "Creativity", text: "We build tools that help people transform ideas into meaningful digital experiences." },
    { icon: Crosshair, title: "Product Focus", text: "We continuously improve our products based on real user needs." },
    { icon: Landmark, title: "Long-Term Thinking", text: "We are building a portfolio of technology products designed for long-term growth." },
];

const DIRECTORS = [
    { name: "Asmita Gorakshnath Patare", role: "Director" },
    { name: "Abhishek Gorakshnath Patare", role: "Director" },
];

const CORPORATE = [
    ["Company", "ANA INTERNATIONAL PRIVATE LIMITED"],
    ["CIN", "U46301PN2025PTC248911"],
    ["Incorporation Date", "6 December 2025"],
    ["Registered Office", "S. No. 265/3A, Room No. 8, Jadhav Mala, Ahmednagar, Ahmed Nagar – 414001, Maharashtra, India"],
    ["Directors", "Asmita Gorakshnath Patare · Abhishek Gorakshnath Patare"],
    ["Email", "info@anainternationalpvt.com"],
    ["Phone", "+91 87998 43634"],
];

export default function About() {
    return (
        <div data-testid="about-page">
            <SEO
                title="About ANA International Private Limited | Technology Company"
                description="ANA International Private Limited is a technology-oriented company in India building AI-powered digital products, applications and web platforms, including Lyanka AI."
                path="/about"
            />

            <section className="relative pt-40 pb-20 overflow-hidden">
                <div className="absolute inset-0 grid-bg" />
                <div className="max-w-7xl mx-auto px-6 lg:px-10 relative z-10">
                    <Reveal>
                        <Eyebrow>About Us</Eyebrow>
                        <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl mt-6 leading-tight max-w-3xl">
                            About <span className="gold-text">ANA International</span>
                        </h1>
                        <p className="text-[#a3a29b] text-base sm:text-lg leading-relaxed mt-8 max-w-3xl" data-testid="about-intro">
                            ANA INTERNATIONAL PRIVATE LIMITED is a technology-oriented company focused on building digital
                            products, applications and web platforms for emerging markets and modern users. The company
                            develops proprietary technology products and operates them through scalable digital business
                            models, including subscription-based services.
                        </p>
                        <p className="text-[#a3a29b] text-base sm:text-lg leading-relaxed mt-5 max-w-3xl">
                            Its current product, Lyanka AI, represents the company's entry into AI-powered creative
                            technology, with a focus on making image and video generation more accessible to creators,
                            filmmakers, editors, marketers and digital storytellers. With a product-first approach,
                            ANA INTERNATIONAL aims to build and launch multiple technology platforms over time.
                        </p>
                    </Reveal>
                </div>
            </section>

            {/* MISSION & VISION */}
            <section className="py-20 bg-[#0d0d0f] border-y border-white/[0.07]">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 grid md:grid-cols-2 gap-6">
                    <Reveal>
                        <div className="card-line rounded-3xl p-10 h-full" data-testid="mission-card">
                            <Compass size={24} className="text-[#e8c766]" />
                            <p className="mono-label mt-6">Mission</p>
                            <p className="font-display text-xl sm:text-2xl leading-relaxed mt-4 text-[#f5f4f0]">
                                “To build useful, accessible and scalable digital products that turn emerging
                                technologies into practical experiences for everyday users and creative professionals.”
                            </p>
                        </div>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <div className="card-line rounded-3xl p-10 h-full" data-testid="vision-card">
                            <Eye size={24} className="text-[#e8c766]" />
                            <p className="mono-label mt-6">Vision</p>
                            <p className="font-display text-xl sm:text-2xl leading-relaxed mt-4 text-[#f5f4f0]">
                                “To build a global portfolio of innovative digital products across artificial
                                intelligence, creativity and digital entertainment.”
                            </p>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* VALUES */}
            <section className="py-24">
                <div className="max-w-7xl mx-auto px-6 lg:px-10">
                    <Reveal>
                        <Eyebrow>Our Values</Eyebrow>
                        <h2 className="font-display font-bold text-3xl sm:text-4xl mt-5">What guides how we build</h2>
                    </Reveal>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-12" data-testid="values-grid">
                        {VALUES.map((v, i) => (
                            <Reveal key={v.title} delay={i * 0.07}>
                                <div className="card-line rounded-2xl p-8 h-full">
                                    <v.icon size={22} className="text-[#e8c766]" />
                                    <h3 className="font-display font-semibold text-base mt-5 uppercase tracking-wide">{v.title}</h3>
                                    <p className="text-sm text-[#a3a29b] leading-relaxed mt-3">{v.text}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* LEADERSHIP */}
            <section className="py-24 bg-[#0d0d0f] border-y border-white/[0.07]">
                <div className="max-w-7xl mx-auto px-6 lg:px-10">
                    <Reveal>
                        <Eyebrow>Leadership</Eyebrow>
                        <h2 className="font-display font-bold text-3xl sm:text-4xl mt-5">Board of Directors</h2>
                    </Reveal>
                    <div className="grid sm:grid-cols-2 gap-6 mt-12 max-w-3xl" data-testid="directors-grid">
                        {DIRECTORS.map((d, i) => (
                            <Reveal key={d.name} delay={i * 0.1}>
                                <div className="card-line rounded-2xl p-8 flex items-center gap-6">
                                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#e8c766] to-[#96761a] flex items-center justify-center shrink-0">
                                        <span className="font-display font-bold text-lg text-[#0a0a0b]">
                                            {d.name.split(" ").map((w) => w[0]).slice(0, 2).join("")}
                                        </span>
                                    </div>
                                    <div>
                                        <h3 className="font-display font-semibold text-lg text-[#f5f4f0]">{d.name}</h3>
                                        <p className="text-sm text-[#c9a227] mt-1 tracking-wide uppercase">{d.role}</p>
                                    </div>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* CORPORATE INFORMATION */}
            <section className="py-24">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-3 gap-14 items-start">
                    <Reveal>
                        <Eyebrow>Corporate Information</Eyebrow>
                        <h2 className="font-display font-bold text-3xl sm:text-4xl mt-5 leading-tight">Registered & Compliant</h2>
                        <p className="text-[#a3a29b] text-sm leading-relaxed mt-5">
                            ANA INTERNATIONAL PRIVATE LIMITED was incorporated on 6 December 2025 under the
                            Companies Act, 2013.
                        </p>
                        <div className="bg-white rounded-2xl p-6 mt-8 w-fit" data-testid="corporate-logo-card">
                            <img src="/assets/ana-logo.png" alt="ANA International Private Limited official logo" className="w-64" />
                        </div>
                    </Reveal>
                    <Reveal delay={0.1} className="lg:col-span-2">
                        <dl className="card-line rounded-3xl divide-y divide-white/[0.07]" data-testid="corporate-info-table">
                            {CORPORATE.map(([k, v]) => (
                                <div key={k} className="grid sm:grid-cols-[220px_1fr] gap-2 sm:gap-6 px-8 py-5">
                                    <dt className="mono-label !text-[10px] self-center">{k}</dt>
                                    <dd className="text-sm text-[#d8d7d1] leading-relaxed">{v}</dd>
                                </div>
                            ))}
                        </dl>
                    </Reveal>
                </div>
            </section>
        </div>
    );
}
